import { createMetaDef } from '@exodus/asset'
import assert from 'minimalistic-assert'

import { createAssetFactory } from './create-asset.js'

// Move to ethereum-api for config base evm
export const createAssetPluginFactory = (config) => {
  assert(config, 'config is required')
  assert(config.chainId, 'chainId is required')
  assert(config.meta, 'config.meta is required')
  assert(config.meta.explorerUrl, 'meta.explorerUrl is required')

  const chainId = config.chainId
  const { explorerUrl, decimals, ...meta } = config.meta
  const chainIdHex = '0x' + chainId.toString(16)
  const name = `evm${chainIdHex}`.toLowerCase()
  const ticker = name.toUpperCase()
  const tokenType = `EVM_${chainIdHex}_TOKEN`.toUpperCase()

  const units = {
    wei: 0,
    Kwei: 3,
    Mwei: 6,
    Gwei: 9,
    [ticker]: decimals || 18,
  }

  // adds lots of validation in config
  const tokenOverrides = (token) => ({
    ...token,
    assetType: tokenType,
    contract: token.addresses,
    gasLimit: meta.gasLimit || 120e3,
  })

  const blockExplorer = {
    addressUrl: (address) => `${explorerUrl}/address/${encodeURIComponent(address)}`,
    txUrl: (txId) => `${explorerUrl}/tx/${encodeURIComponent(txId)}`,
  }

  const assetType = 'ETHEREUM_LIKE'
  const { assetsList } = createMetaDef({
    assetParams: {
      ...meta,
      name,
      ticker,
      units,
      chainId,
      assetType,
      blockExplorer,
    },
    tokensParams: meta.tokens || [],
    tokenOverrides,
  })
  const feeDataConfig = Object.fromEntries(
    Object.entries({
      gasPrice: config.gasPrice,
      baseFeePerGas: config.baseFeePerGas,
      eip1559Enabled: config.eip1559Enabled,
      ...config.feeData,
    }).filter(([_, value]) => value !== undefined)
  )
  const createAsset = createAssetFactory({
    assetsList,
    feeDataConfig,
    ...config.plugin,
  })

  return { name, chainId, createAsset }
}

// https://docs.metamask.io/wallet/reference/wallet_addethereumchain/
export const fromAddEthereumChainParameterToFactoryParams = (params) => {
  assert(params.chainId, 'chainId is required')
  assert(params.chainName, 'chainName is required')
  assert(params.rpcUrls?.[0], 'rpcUrls is required')
  assert(params.blockExplorerUrls?.[0], 'blockExplorerUrls is required')
  assert(params.nativeCurrency, 'nativeCurrency is required')
  assert(params.nativeCurrency.symbol, 'nativeCurrency.symbol is required')
  assert(params.nativeCurrency.decimals, 'nativeCurrency.decimals is required')

  const chainId = parseInt(params.chainId.replace('0x', ''), 16)

  // obvious coloring:)
  const color =
    params.color ||
    '#' +
      (chainId % 256).toString(16) +
      ((chainId + 50) % 256).toString(16) +
      ((chainId + 100) % 256).toString(16)
  return {
    chainId,
    meta: {
      explorerUrl: params.blockExplorerUrls[0],
      displayNetworkName: params.chainName,
      displayName: params.nativeCurrency.name || params.nativeCurrency.symbol,
      displayTicker: params.nativeCurrency.symbol,
      decimals: params.nativeCurrency.decimals,
      primaryColor: color,
      gradientColors: [color, color],
      gradientCoords: {
        x1: '0%',
        y1: '0%',
        x2: '100%',
        y2: '100%',
      },
    },
    plugin: {
      serverUrl: params.rpcUrls?.[0],
      confirmationsNumber: 5,
      monitorType: 'no-history',
      isTestnet: params.isTestnet,
      ...params.plugin, // extra plugin configuration
    },
    feeData: params.feeData,
    baseFeePerGas: params.baseFeePerGas,
    gasPrice: params.gasPrice,
    eip1559Enabled: params.eip1559Enabled,
  }
}
